// ============================================================================
// PERMISSION HOOKS
// ============================================================================
// React hooks for accessing auth state and permission engine.

import { useMemo } from 'react';
import { useAuthContext } from '../contexts/AuthContext';
import { buildNavigationFromPermissions } from '../auth/permissionEngine';
import type { PermissionEngine, NavigationSection } from '../auth/permissionEngine';

/**
 * Full auth context hook
 */
export const useAuth = () => useAuthContext();

/**
 * Just the permission engine (most common use)
 */
export const usePermissions = (): PermissionEngine => {
  const { permissionEngine } = useAuthContext();
  return permissionEngine;
};

/**
 * Single permission check (returns boolean)
 */
export const useHasPermission = (resource: string, action: string): boolean => {
  const engine = usePermissions();
  return engine.hasAction(resource, action);
};

/**
 * Check if user has access to a specific module
 */
export const useHasModuleAccess = (moduleCode: string): boolean => {
  const engine = usePermissions();
  return engine.hasModuleAccess(moduleCode);
};

/**
 * Navigation: transforms permissionContext into NavigationSection[] for main_layout
 */
export const useNavigationPermissions = (): NavigationSection[] | null => {
  const { permissionContext } = useAuthContext();
  return useMemo(
    () => (permissionContext ? buildNavigationFromPermissions(permissionContext) : null),
    [permissionContext]
  );
};

/**
 * Scope context — entity, business unit, financial year selections
 */
export const useScopeContext = () => {
  const {
    selectedEntity,
    setSelectedEntity,
    selectedBusinessUnit,
    setSelectedBusinessUnit,
    selectedFinancialYear,
    setSelectedFinancialYear,
    accessibleEntities,
    accessibleBusinessUnits,
    accessibleFinancialYears,
    scopeConfiguration,
  } = useAuthContext();

  return {
    selectedEntity,
    setSelectedEntity,
    selectedBusinessUnit,
    setSelectedBusinessUnit,
    selectedFinancialYear,
    setSelectedFinancialYear,
    accessibleEntities,
    accessibleBusinessUnits,
    accessibleFinancialYears,
    scopeConfiguration,
  };
};
